import { PageLoader } from '@/components/ui/loader';
import { useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, CheckSquare, Mail, Phone, Plus, Trash2, Users, Workflow } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { EnrollmentRow } from './record-tools';
import {
  ENROLLMENT_STATUS,
  useDeleteSequence,
  useEnrollmentAction,
  useEnrollments,
  useMailboxes,
  useMergeFields,
  useSaveSequence,
  useSequence,
  useSequences,
  useTemplates,
  type Sequence,
  type SequenceSettings,
  type SequenceStep,
} from './api';

let counter = 0;
const newId = () => `st${Date.now().toString(36).slice(-4)}${(counter += 1)}`;

const DEFAULT_SETTINGS: SequenceSettings = {
  mailboxChannelId: null,
  sendFrom: 9,
  sendTo: 18,
  weekdaysOnly: true,
  timeZone: 'Asia/Dubai',
  stopOnMeeting: true,
  track: true,
};

/** A follow-up cadence that works for most UAE B2B enquiries. */
function starter(): SequenceStep[] {
  return [
    {
      id: newId(),
      type: 'email',
      delayDays: 0,
      subject: 'Following up on your enquiry',
      body: 'Hi {{firstName|there}},\n\nThank you for your interest. I would love to understand what you are looking for and show you how we can help.\n\nWould a 20-minute call this week work? You can pick a time here: {{sender.meetingLink}}\n\nBest regards,\n{{sender.name}}\n{{sender.phone}}',
    },
    { id: newId(), type: 'call', delayDays: 2, title: 'Call {{firstName}} about their enquiry', note: null },
    {
      id: newId(),
      type: 'email',
      delayDays: 1,
      subject: '',
      body: 'Hi {{firstName|there}},\n\nJust making sure my last email reached you. Happy to share pricing or a short demo — whichever is more useful.\n\n{{sender.name}}',
    },
    {
      id: newId(),
      type: 'email',
      delayDays: 4,
      subject: '',
      body: 'Hi {{firstName|there}},\n\nI will not keep filling your inbox — if now is not the right time, just let me know and I will check back later.\n\n{{sender.name}}',
    },
  ];
}

/**
 * Sequences (HubSpot): automated, personal follow-up. The page lists them
 * with their reply rates; opening one shows who is in it and where.
 */
export function SequencesPage() {
  const { data: sequences = [], isLoading } = useSequences();
  const [editing, setEditing] = useState<Sequence | 'new' | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  return (
    <div>
      <PageHeader
        title="Sequences"
        description="Follow-up emails and call tasks spaced over days, sent as you on working days. Anyone who replies or books a meeting comes out on their own."
        breadcrumbs={[{ label: 'Sales hub' }, { label: 'Sequences' }]}
        actions={
          <Can permission="crm.sequence.create">
            <Button onClick={() => setEditing('new')}>
              <Plus /> New sequence
            </Button>
          </Can>
        }
      />

      {isLoading ? (
        <PageLoader />
      ) : sequences.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center">
          <Workflow className="mx-auto h-10 w-10 text-primary/60" />
          <p className="mt-3 font-semibold">No sequences yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Start from the enquiry follow-up: an email today, a call on day two, two gentle nudges after.
          </p>
          <Can permission="crm.sequence.create">
            <Button className="mt-4" onClick={() => setEditing('new')}>
              Create a sequence
            </Button>
          </Can>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sequences.map((sequence) => (
            <Card key={sequence.id} className="cursor-pointer transition-shadow hover:shadow-md" onClick={() => setViewing(sequence.id)}>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{sequence.name}</p>
                    <p className="text-xs text-muted-foreground">{sequence.stepCount} steps</p>
                  </div>
                  <Badge className={sequence.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}>
                    {sequence.isActive ? 'On' : 'Off'}
                  </Badge>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <Stat label="Enrolled" value={sequence.enrolled} />
                  <Stat label="Active" value={sequence.active} />
                  <Stat label="Opened" value={`${sequence.openRate}%`} />
                  <Stat label="Replied" value={`${sequence.replyRate}%`} />
                </div>
                <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
                  <Can permission="crm.sequence.update">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(sequence)}>
                      Edit
                    </Button>
                  </Can>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && <SequenceEditor sequence={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {viewing && <SequenceDetail id={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg bg-muted/40 px-1 py-2">
      <p className="text-base font-bold">{value}</p>
      <p className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</p>
    </div>
  );
}

function SequenceDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: sequence } = useSequence(id);
  const [status, setStatus] = useState('');
  const { data: rows = [], isLoading } = useEnrollments({ sequenceId: id, status: status || undefined });
  const act = useEnrollmentAction();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sequence?.name ?? 'Sequence'}</DialogTitle>
          <DialogDescription>{sequence?.description ?? 'Who is in it, and how each step is doing.'}</DialogDescription>
        </DialogHeader>
        {sequence && (
          <ol className="space-y-1.5">
            {sequence.steps.map((step, index) => (
              <li key={step.id} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <StepIcon type={step.type} />
                  <span className="truncate">
                    {step.type === 'email' ? step.subject || 'Reply in the same thread' : step.title}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  +{step.delayDays}d
                  {step.type === 'email' && sequence.stepStats[index]
                    ? ` · ${sequence.stepStats[index].sent} sent · ${sequence.stepStats[index].opened} opened`
                    : ''}
                </span>
              </li>
            ))}
          </ol>
        )}
        <div className="flex items-center justify-between gap-2 pt-2">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-4 w-4" /> Enrolled
          </p>
          <Select className="h-8 w-44 text-xs" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Everyone</option>
            {Object.entries(ENROLLMENT_STATUS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        {isLoading ? (
          <PageLoader />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody yet. Enrol people from a lead or contact page, or select several in the list.
          </p>
        ) : (
          <ul className="divide-y">
            {rows.map((row) => (
              <EnrollmentRow key={row.id} row={row} showName onAct={(action) => act.mutate({ id: row.id, action })} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StepIcon({ type }: { type: SequenceStep['type'] }) {
  const Icon = type === 'email' ? Mail : type === 'call' ? Phone : CheckSquare;
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

function SequenceEditor({ sequence, onClose }: { sequence: Sequence | null; onClose: () => void }) {
  const save = useSaveSequence();
  const remove = useDeleteSequence();
  const { data: mailboxes = [] } = useMailboxes();
  const { data: templates = [] } = useTemplates('TEMPLATE');
  const { data: fields = [] } = useMergeFields();
  const [name, setName] = useState(sequence?.name ?? 'Enquiry follow-up');
  const [description, setDescription] = useState(sequence?.description ?? '');
  const [steps, setSteps] = useState<SequenceStep[]>(sequence?.steps ?? starter());
  const [settings, setSettings] = useState<SequenceSettings>({ ...DEFAULT_SETTINGS, ...(sequence?.settings ?? {}) });
  const [isActive, setIsActive] = useState(sequence?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const update = (index: number, patch: Partial<SequenceStep>) =>
    setSteps((current) => current.map((step, at) => (at === index ? ({ ...step, ...patch } as SequenceStep) : step)));
  const move = (index: number, by: number) =>
    setSteps((current) => {
      const next = [...current];
      const [step] = next.splice(index, 1);
      next.splice(index + by, 0, step);
      return next;
    });
  const add = (type: SequenceStep['type']) =>
    setSteps((current) => [
      ...current,
      type === 'email'
        ? { id: newId(), type, delayDays: 2, subject: '', body: 'Hi {{firstName|there}},\n\n\n\n{{sender.name}}' }
        : { id: newId(), type, delayDays: 2, title: type === 'call' ? 'Call {{firstName}}' : 'Follow up with {{firstName}}', note: null },
    ]);

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: sequence?.id, name, description: description || null, steps, settings, isActive });
      toast.success('Sequence saved');
      onClose();
    } catch (failure) {
      setError(failure instanceof ApiRequestError ? failure.message : 'Could not save');
    }
  };

  let day = 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sequence ? 'Edit sequence' : 'New sequence'}</DialogTitle>
          <DialogDescription>
            Each step waits the given days after the one before. Leave a follow-up subject blank to reply in the same thread.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Send from</Label>
              <Select
                value={settings.mailboxChannelId ?? ''}
                onChange={(event) => setSettings((current) => ({ ...current, mailboxChannelId: event.target.value || null }))}
              >
                <option value="">DovoCRM mail, replies to the rep</option>
                {mailboxes.map((box) => (
                  <option key={box.id} value={box.id}>
                    {box.name}
                    {box.address ? ` — ${box.address}` : ''}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="When to use it" />
          </div>

          <div className="space-y-2">
            {steps.map((step, index) => {
              day += step.delayDays;
              return (
                <div key={step.id} className="rounded-xl border p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <StepIcon type={step.type} />
                      Step {index + 1} · {step.type === 'email' ? 'Email' : step.type === 'call' ? 'Call task' : 'Task'}
                      <span className="text-xs font-normal text-muted-foreground">day {day}</span>
                    </span>
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-muted-foreground">Wait</Label>
                      <Input
                        type="number"
                        min={0}
                        max={90}
                        className="h-8 w-16"
                        value={step.delayDays}
                        onChange={(event) => update(index, { delayDays: Math.max(0, Number(event.target.value) || 0) })}
                      />
                      <span className="text-xs text-muted-foreground">days</span>
                      <Button size="sm" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)}>
                        <ArrowUp />
                      </Button>
                      <Button size="sm" variant="ghost" disabled={index === steps.length - 1} onClick={() => move(index, 1)}>
                        <ArrowDown />
                      </Button>
                      <Button size="sm" variant="ghost" disabled={steps.length === 1} onClick={() => setSteps((current) => current.filter((_, at) => at !== index))}>
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                  {step.type === 'email' ? (
                    <div className="space-y-2">
                      {templates.length > 0 && (
                        <Select
                          className="h-8 text-xs"
                          value={step.templateId ?? ''}
                          onChange={(event) => {
                            const template = templates.find((item) => item.id === event.target.value);
                            update(index, template ? { templateId: template.id, subject: template.subject ?? step.subject, body: template.body } : { templateId: null });
                          }}
                        >
                          <option value="">Write it here</option>
                          {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                              Template: {template.name}
                            </option>
                          ))}
                        </Select>
                      )}
                      <Input
                        placeholder={index === 0 ? 'Subject' : 'Subject — blank replies in the same thread'}
                        value={step.subject}
                        onChange={(event) => update(index, { subject: event.target.value })}
                      />
                      <Textarea rows={6} value={step.body} onChange={(event) => update(index, { body: event.target.value })} />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input value={step.title} onChange={(event) => update(index, { title: event.target.value })} />
                      <Input
                        placeholder="Note for the rep (optional)"
                        value={step.note ?? ''}
                        onChange={(event) => update(index, { note: event.target.value || null })}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => add('email')}>
                <Mail /> Add email
              </Button>
              <Button size="sm" variant="outline" onClick={() => add('call')}>
                <Phone /> Add call task
              </Button>
              <Button size="sm" variant="outline" onClick={() => add('task')}>
                <CheckSquare /> Add task
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Placeholders: {fields.map((field) => `{{${field.key}}}`).join(' ')}
            </p>
          </div>

          <div className="grid gap-3 rounded-xl bg-muted/30 p-3 sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Label className="w-28 shrink-0">Send between</Label>
              <Select value={settings.sendFrom} onChange={(event) => setSettings((current) => ({ ...current, sendFrom: Number(event.target.value) }))}>
                {Array.from({ length: 24 }, (_, hour) => (
                  <option key={hour} value={hour}>{`${String(hour).padStart(2, '0')}:00`}</option>
                ))}
              </Select>
              <span>–</span>
              <Select value={settings.sendTo} onChange={(event) => setSettings((current) => ({ ...current, sendTo: Number(event.target.value) }))}>
                {Array.from({ length: 24 }, (_, hour) => hour + 1).map((hour) => (
                  <option key={hour} value={hour}>{`${String(hour).padStart(2, '0')}:00`}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Time zone</Label>
              <Select value={settings.timeZone} onChange={(event) => setSettings((current) => ({ ...current, timeZone: event.target.value }))}>
                {['Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kolkata', 'Asia/Karachi', 'Europe/London', 'America/New_York'].map((zone) => (
                  <option key={zone}>{zone}</option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={settings.weekdaysOnly} onCheckedChange={(value) => setSettings((current) => ({ ...current, weekdaysOnly: value }))} />
              Monday to Friday only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={settings.stopOnMeeting} onCheckedChange={(value) => setSettings((current) => ({ ...current, stopOnMeeting: value }))} />
              Stop when they book a meeting
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={settings.track} onCheckedChange={(value) => setSettings((current) => ({ ...current, track: value }))} />
              Track opens and clicks
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={isActive} onCheckedChange={setIsActive} /> Sequence is on
            </label>
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter className={cn(sequence && 'sm:justify-between')}>
          {sequence && (
            <Can permission="crm.sequence.update">
              <Button
                variant="ghost"
                className="text-destructive"
                onClick={() => {
                  if (!window.confirm('Delete this sequence? Everyone in it stops.')) return;
                  remove.mutate(sequence.id, { onSuccess: onClose });
                }}
              >
                <Trash2 /> Delete
              </Button>
            </Can>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
